import { useCallback, useEffect, useState } from "react";
import { DataTableColums } from "../../../Components/Extra/DataTable/DataTable";
import Borrowing from "../../../Models/Borrowing";
import BorrowingService from "../../../Services/Borrowing/BorrowingService";
import { DatatableFilter } from "../../../Models/DatatableFilter";
import DataTableWithPaginatedData from "../../../Components/Extra/DataTable/DataTableWithPaginatedData/DataTableWithPaginatedData";
import TransactionResponsiveTable, {
    DataResponsiveTableColums,
} from "../../../Components/Extra/TableResponsive/TransactionResponsiveTable";
import { useAppProvider } from "../../Provider/AppProvider";
import translator from "../../../Components/Extra/Translation/Translate";
import AddGuaranteeModal from "./borrowingModal/addGuarantee/BorrowingModal";
import RefundModal from "./borrowingModal/refund/RefundModal";
import DetailModal from "./borrowingModal/detail/DetailModal";

const BorrowingTable = () => {
    const [borrowings, setBorrowings] = useState<Array<Borrowing>>([]);
    const { setProcessing, finalizeProcessing } = useAppProvider();
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [increment, setIncrementor] = useState<number>(0);
    const [direction, setDirection] = useState<"ASC" | "DESC">("DESC");
    const [filterIndex, setFilterIndex] = useState<number>(0);
    const [perPage, setPerPage] = useState<number>(10);
    const [nbPage, setNbPage] = useState<number>(0);
    const [search, setSearch] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { translate } = translator();

    const colums: DataTableColums[] = [
        {
            index: "date",
            title: "Date",
            content: (row: Borrowing) => {
                return row.created_at;
            },
            filter: true,
            className: "whitespace-nowrap",
        },
        {
            index: "uid",
            title: "Ref",
            content: (row: Borrowing) => {
                return (
                    <span className="font-bold">{row.getBorrowingRef()}</span>
                );
            },
            filter: true,
            className: "whitespace-nowrap",
        },
        {
            index: "amount",
            title: "BORROW.Amount",
            content: (row: Borrowing) => {
                return `${row.borrow_amount} ${row.crypto_borrow.abbreviation}`;
            },
            filter: true,
            className: "whitespace-nowrap text-right",
        },
        {
            index: "guarantee",
            title: "BORROW.Guarantee",
            content: (row: Borrowing) => {
                return `${row.initial_amount_guaranteed} ${row.crypto_guarantee.abbreviation}`;
            },
            filter: true,
            className: "whitespace-nowrap text-right",
        },
        {
            index: "uid",
            title: "BORROW.Total_to_be_repaid",
            content: (row: Borrowing) => {
                return `${row.total_to_be_repaid} ${row.crypto_borrow.abbreviation}`;
            },
            filter: true,
            className: "whitespace-nowrap",
        },
        {
            index: "date",
            title: "BORROW.End_Date",
            content: (row: Borrowing) => {
                return row.end_date;
            },
            filter: true,
            className: "whitespace-nowrap",
        },
        {
            index: "status",
            title: "Transaction.Status",
            content: (row: Borrowing) => {
                return <div>{translate("BORROW", row.status)}</div>;
            },
            filter: true,
            className: "whitespace-nowrap",
        },
        {
            index: "Action",
            title: "Action",
            content: (row: Borrowing) => {
                return (
                    <div className="flex gap-2 w-full justify-center md:!justify-start">
                        <AddGuaranteeModal borrowing={row} />
                        <RefundModal borrowing={row} />
                        <DetailModal borrowing={row} />
                    </div>
                );
            },
            filter: true,
            className: "whitespace-nowrap",
        },
    ];

    const getUserBorrowingPaginate = useCallback(async () => {
        setIsLoading(true);
        const filter: DatatableFilter = {
            page: currentPage,
            size: perPage,
            search: search,
        };

        try {
            const res = await BorrowingService.getUserBorrowingPaginate(filter);
            setBorrowings([...res.data]);
            setNbPage(res.lastPage);
        } catch (err: any) {
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    }, [increment]);

    useEffect(() => {
        setIncrementor(increment + 1);
    }, [currentPage, perPage, search]);

    useEffect(() => {
        setCurrentPage(0);
        setIncrementor(increment + 1);
    }, [direction]);

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page + 1);
    }, []);

    const handlePerRowsChange = useCallback((newPerPage: number) => {
        setPerPage(newPerPage);
    }, []);

    const onSearchInputChange = useCallback(async (txt: string) => {
        setSearch(txt);
    }, []);

    useEffect(() => {
        setProcessing(true);
        Promise.all([getUserBorrowingPaginate()]).then(() => {
            finalizeProcessing();
        });
    }, [getUserBorrowingPaginate]);

    return (
        <>
            {isLoading && <div>Loading...</div>}
            <DataTableWithPaginatedData
                className="table-report -mt-2 table "
                columns={colums}
                data={borrowings}
                handlePaginationChange={handlePageChange}
                handlePerRowsChange={handlePerRowsChange}
                onSearchInputChange={onSearchInputChange}
                nbPage={nbPage}
                direction={direction}
                setDirection={setDirection}
                filterIndex={filterIndex}
                setFilterIndex={setFilterIndex}
                exportCSV={false}
                exportPDF={false}
            />
        </>
    );
};

export default BorrowingTable;
